class MLPurchaseOptions extends HTMLElement {
  connectedCallback() {
    this.events?.abort();
    this.events = new AbortController();
    this.config = JSON.parse(this.querySelector('[data-purchase-data]').textContent);
    this.busy = false;
    this.querySelectorAll('[data-bag-select]').forEach(select => {
      if (!select.selectedOptions[0] || select.selectedOptions[0].disabled) {
        const first = [...select.options].find(option => !option.disabled);
        if (first) select.value = first.value;
      }
    });
    this.addEventListener('change', event => this.updateSelection(event), { signal: this.events.signal });
    this.update();
    this.dataset.ready = 'true';
  }

  disconnectedCallback() {
    this.events?.abort();
    this.restoreSelectionScroll?.();
  }


  updateSelection(event) {
    this.restoreSelectionScroll?.();
    const input = event.target;
    const row = input instanceof Element && input.matches('input[name^="ml_pack_"]')
      ? input.closest('.ml-purchase__row') : null;
    if (!row || input.value === this.mode) {
      this.update();
      return;
    }
    let scroller = this.parentElement;
    while (scroller && scroller !== document.documentElement) {
      const overflow = getComputedStyle(scroller).overflowY;
      if (/(auto|scroll)/.test(overflow) && scroller.scrollHeight > scroller.clientHeight) break;
      scroller = scroller.parentElement;
    }
    scroller = scroller && scroller !== document.documentElement ? scroller : document.scrollingElement;
    if (!scroller) {
      this.update();
      return;
    }
    const scrollTop = scroller.scrollTop;
    const properties = ['overflow-anchor', 'scroll-behavior'];
    const previous = properties.map(name => [name, scroller.style.getPropertyValue(name), scroller.style.getPropertyPriority(name)]);
    scroller.style.setProperty('overflow-anchor', 'none', 'important');
    scroller.style.setProperty('scroll-behavior', 'auto', 'important');
    const holdPosition = () => {
      if (Math.abs(scroller.scrollTop - scrollTop) > .5) scroller.scrollTop = scrollTop;
    };
    const restore = () => {
      previous.forEach(([name, value, priority]) => {
        if (value) scroller.style.setProperty(name, value, priority);
        else scroller.style.removeProperty(name);
      });
      this.restoreSelectionScroll = null;
    };
    this.update();
    holdPosition();
    const frame = requestAnimationFrame(() => {
      if (this.isConnected) holdPosition();
      restore();
    });
    this.restoreSelectionScroll = () => {
      cancelAnimationFrame(frame);
      restore();
    };
  }

  money(cents) {
    return new Intl.NumberFormat(this.dataset.locale, { style: 'currency', currency: this.dataset.currency, minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2 }).format(cents / 100);
  }

  portion(cents, servings) {
    const price = new Intl.NumberFormat(this.dataset.locale, { style: 'currency', currency: this.dataset.currency }).format(cents / servings / 100);
    return this.config.perServing.replace('[price]', price);
  }

  update() {
    this.mode = this.querySelector('input[name^="ml_pack_"]:checked').value;
    this.method = this.querySelector('input[name^="ml_method_"]:checked').value;
    this.querySelectorAll('[data-choice]').forEach(card => card.classList.toggle('is-selected', card.dataset.choice === this.mode));
    const gifts = this.querySelector('[data-purchase-gifts]');
    const selectedCard = this.querySelector('[data-choice].is-selected');
    if (gifts && selectedCard) {
      gifts.classList.remove('ml-purchase-gifts--single');
      gifts.hidden = this.mode !== 'bundle' && this.method !== 'subscription';
      if (gifts.parentElement !== selectedCard) selectedCard.append(gifts);
    }
    this.querySelector('input[name^="ml_pack_"][value="single"]').tabIndex = this.mode === 'single' ? -1 : 0;
    const bagPanel = this.querySelector('[data-bags]');
    if (bagPanel) bagPanel.hidden = this.mode !== 'bundle';
    this.querySelector('[data-methods]').hidden = this.mode !== 'single';
    const panel = this.querySelector('[data-plan-panel]');
    if (panel) panel.hidden = this.mode !== 'single' || this.method !== 'subscription';
    const bagSelects = [...this.querySelectorAll('[data-bag-select]')];
    this.bags = bagSelects.length ? bagSelects.map(select => {
      const product = this.config.products.find(p => String(p.id) === select.value);
      const image = select.closest('.ml-purchase__bag').querySelector('img');
      if (product && image && image.dataset.product !== String(product.id)) {
        image.src = product.image;
        image.removeAttribute('srcset');
        image.alt = product.title;
        image.dataset.product = String(product.id);
      }
      return product;
    }) : Array(3).fill(this.config.products.find(p => String(p.id) === this.dataset.variantId));
    const validBags = this.bags.filter(Boolean);
    const completeBundle = validBags.length === 3 && validBags.every(p => Number.isFinite(p.price));
    const basePrice = Number(this.dataset.price) || 0;
    const was = completeBundle ? validBags.reduce((sum, p) => sum + p.price, 0) : basePrice * 3;
    const price = completeBundle ? was - Math.min(...validBags.map(p => p.price)) : basePrice * 2;
    this.querySelector('[data-bundle-was]').textContent = this.money(was);
    this.querySelector('[data-bundle-price]').textContent = this.money(price);
    this.querySelector('[data-bundle-portion]').textContent = this.portion(price, 90);
    const select = this.querySelector('[data-plan-select]');
    this.plan = this.config.plans.find(plan => String(plan.id) === select?.value);
    const singlePrice = this.method === 'subscription' && this.plan ? this.plan.price : Number(this.dataset.price);
    const lowestSinglePrice = Math.min(Number(this.dataset.price), ...this.config.plans.map(plan => plan.price));
    this.querySelector('[data-single-summary]').textContent = this.dataset.priceFrom.replace('[price]', this.money(lowestSinglePrice));
    this.querySelector('[data-one-time-price]').textContent = this.money(Number(this.dataset.price));
    this.querySelector('[data-one-time-portion]').textContent = this.portion(Number(this.dataset.price), 30);
    const stickyPrice = this.closest('.shopify-section')?.querySelector('[data-testid="sticky-price-display"] .price');
    if (stickyPrice) stickyPrice.textContent = this.money(this.mode === 'bundle' ? price : singlePrice);
    if (this.plan) {
      this.querySelector('[data-subscription-was]').textContent = this.money(Number(this.dataset.price));
      this.querySelector('[data-subscription-price]').textContent = this.money(this.plan.price);
      this.querySelector('[data-subscription-portion]').textContent = this.portion(this.plan.price, 30);
      this.querySelector('[data-plan-terms]').textContent = this.config.terms.replaceAll('[first]', this.money(this.plan.price)).replaceAll('[recurring]', this.money(this.plan.recurring)).replaceAll('[first_discount]', String(Math.round((1 - this.plan.price / Number(this.dataset.price)) * 100))).replaceAll('[recurring_discount]', String(Math.round((1 - this.plan.recurring / Number(this.dataset.price)) * 100)));
    }
  }

  async prepareFormData(data) {
    this.busy = true;
    const button = this.closest('form').querySelector('[type="submit"]');
    this.wasDisabled = button?.disabled;
    if (button) button.disabled = true;
    this.querySelectorAll('fieldset').forEach(fieldset => { fieldset.disabled = true; });
    this.querySelector('[role="alert"]').hidden = true;
    this.cartLines = null;
    try {
      if (this.mode === 'bundle') {
        if (this.bags.length !== 3 || this.bags.some(p => !p?.available)) throw new Error('Unavailable selection');
        const root = window.Shopify.routes.root;
        const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!response.ok) throw new Error('Cannot read cart');
        const cart = await response.json();
        this.discountCodes = new Set((cart.discount_codes || []).map(d => d.code));
        if (this.dataset.discountCode) this.discountCodes.add(this.dataset.discountCode);
        data.delete('id');
        data.delete('quantity');
        data.delete('selling_plan');
        const counts = new Map();
        this.bags.forEach(p => counts.set(String(p.id), (counts.get(String(p.id)) || 0) + 1));
        this.cartLines = [...counts].map(([merchandiseId, quantity]) => ({ merchandiseId, quantity }));
        this.cartLines.forEach((line, i) => {
          data.set(`items[${i}][id]`, line.merchandiseId);
          data.set(`items[${i}][quantity]`, String(line.quantity));
        });
      } else {
        data.set('id', this.dataset.variantId);
        data.set('quantity', '1');
        if (this.method === 'subscription' && this.plan) data.set('selling_plan', String(this.plan.id));
        else data.delete('selling_plan');
      }
    } catch (error) {
      this.release();
      throw error;
    }
  }

  release() {
    this.busy = false;
    const button = this.closest('form').querySelector('[type="submit"]');
    if (button) button.disabled = this.wasDisabled || false;
    this.querySelectorAll('fieldset').forEach(fieldset => { fieldset.disabled = false; });
  }

  showError() {
    const error = this.querySelector('[role="alert"]');
    error.textContent = this.dataset.error;
    error.hidden = false;
  }

  async finishCart(sectionIds) {
    if (!this.cartLines) return null;
    const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: [...this.discountCodes].join(','), sections: sectionIds, sections_url: window.location.pathname })
    });
    const cart = await response.json();
    if (!response.ok || cart.errors) throw new Error('Cannot apply offer');
    return cart;
  }
}

if (!customElements.get('ml-purchase-options')) customElements.define('ml-purchase-options', MLPurchaseOptions);

class MLPurchaseSupport extends HTMLElement {
  connectedCallback() {
    this.events?.abort();
    this.events = new AbortController();
    const signal = this.events.signal;
    this.index = this.index || 0;
    this.slides = [...this.querySelectorAll('[data-review-slide]')];
    this.dots = [...this.querySelectorAll('[data-review-dot]')];
    this.querySelector('[data-review-prev]')?.addEventListener('click', () => this.show(this.index - 1), { signal });
    this.querySelector('[data-review-next]')?.addEventListener('click', () => this.show(this.index + 1), { signal });
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.show(i), { signal }));
    const track = this.querySelector('[data-review-track]');
    track?.addEventListener('pointerdown', event => { if (event.pointerType !== 'mouse') this.start = { x: event.clientX, y: event.clientY }; }, { signal });
    track?.addEventListener('pointerup', event => {
      if (!this.start) return;
      const dx = event.clientX - this.start.x;
      const dy = event.clientY - this.start.y;
      this.start = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) this.show(this.index + (dx < 0 ? 1 : -1));
    }, { signal });
    track?.addEventListener('pointercancel', () => { this.start = null; }, { signal });
    this.show(this.index);
    this.startDeliveryClock();
  }

  disconnectedCallback() {
    this.events?.abort();
    clearInterval(this.deliveryTimer);
  }

  startDeliveryClock() {
    clearInterval(this.deliveryTimer);
    this.updateDelivery();
    if (this.querySelector('[data-countdown]')) {
      this.deliveryTimer = setInterval(() => this.updateDelivery(), 60000);
    }
  }

  updateDelivery() {
    const delivery = this.querySelector('[data-delivery-date]');
    if (!delivery) return;
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Berlin', year: 'numeric', month: 'numeric', day: 'numeric',
      hour: 'numeric', minute: 'numeric', second: 'numeric', hourCycle: 'h23'
    });
    const parts = Object.fromEntries(formatter.formatToParts(now).map(p => [p.type, p.value]));
    const cutoff = Math.max(0, Math.min(23, Number(delivery.dataset.cutoff) || 0));
    const dispatch = new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), cutoff));
    if (Number(parts.hour) >= cutoff) dispatch.setUTCDate(dispatch.getUTCDate() + 1);
    while ([0, 6].includes(dispatch.getUTCDay())) dispatch.setUTCDate(dispatch.getUTCDate() + 1);
    let cutoffEpoch = dispatch.getTime();
    for (let i = 0; i < 3; i++) {
      const local = Object.fromEntries(formatter.formatToParts(new Date(cutoffEpoch)).map(p => [p.type, p.value]));
      const represented = Date.UTC(Number(local.year), Number(local.month) - 1, Number(local.day), Number(local.hour), Number(local.minute), Number(local.second));
      cutoffEpoch += dispatch.getTime() - represented;
    }
    const date = new Date(dispatch);
    for (let remaining = Math.max(1, Number(delivery.dataset.days) || 2); remaining > 0;) {
      date.setUTCDate(date.getUTCDate() + 1);
      if (![0, 6].includes(date.getUTCDay())) remaining--;
    }
    const locale = this.lang || document.documentElement.lang || 'de';
    if (!delivery.hasAttribute('data-countdown')) {
      const formatted = new Intl.DateTimeFormat(locale, { timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long' }).format(date);
      delivery.textContent = delivery.dataset.template.replace('[date]', formatted);
      return;
    }
    const minutes = Math.max(1, Math.ceil((cutoffEpoch - now.getTime()) / 60000));
    const values = {
      '[weekday]': new Intl.DateTimeFormat(locale, { timeZone: 'UTC', weekday: 'long' }).format(date),
      '[date]': new Intl.DateTimeFormat(locale, { timeZone: 'UTC', day: 'numeric', month: 'long' }).format(date),
      '[countdown]': Math.floor(minutes / 60) + ' Std. ' + (minutes % 60) + ' Min.',
      '[carrier]': delivery.dataset.carrier || 'DHL'
    };
    const renderText = (target, template) => {
      const fragment = document.createDocumentFragment();
      for (const part of template.split(/(\[weekday\]|\[date\]|\[countdown\]|\[carrier\])/g)) {
        if (part in values && part !== '[carrier]') {
          const strong = document.createElement('strong');
          strong.textContent = values[part];
          fragment.append(strong);
        } else {
          fragment.append(document.createTextNode(values[part] ?? part));
        }
      }
      target.replaceChildren(fragment);
    };
    renderText(delivery.querySelector('[data-delivery-heading]') || delivery, delivery.dataset.template);
    const deadline = delivery.querySelector('[data-delivery-deadline]');
    if (deadline) renderText(deadline, deadline.dataset.template);
  }

  show(index) {
    if (!this.slides.length) return;
    this.index = (index + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, i) => { slide.hidden = i !== this.index; });
    this.dots.forEach((dot, i) => dot.setAttribute('aria-pressed', String(i === this.index)));
    const status = this.querySelector('[data-review-status]');
    if (status) status.textContent = this.dots[this.index]?.getAttribute('aria-label') || '';
  }
}

if (!customElements.get('ml-purchase-support')) customElements.define('ml-purchase-support', MLPurchaseSupport);


class MLProductAccordions extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.addEventListener('click', event => this.togglePanel(event), { signal: this.controller.signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
    if (this.restoreScroll) this.restoreScroll();
  }

  togglePanel(event) {
    if (event.defaultPrevented || event.button > 0 || !(event.target instanceof Element)) return;
    const summary = event.target.closest('summary');
    const panel = summary?.parentElement;
    if (!summary || !panel?.matches('.ml-purchase-support__information > details') || panel.closest('ml-product-accordions') !== this) return;
    if (event.target.closest('a, button, input, select, textarea')) return;
    event.preventDefault();
    this.restoreScroll?.();
    let scroller = summary.parentElement;
    while (scroller && scroller !== document.documentElement) {
      const overflow = getComputedStyle(scroller).overflowY;
      if (/(auto|scroll)/.test(overflow) && scroller.scrollHeight > scroller.clientHeight) break;
      scroller = scroller.parentElement;
    }
    scroller = scroller && scroller !== document.documentElement ? scroller : document.scrollingElement;
    if (!scroller) return;
    const anchor = scroller.style.getPropertyValue('overflow-anchor');
    const anchorPriority = scroller.style.getPropertyPriority('overflow-anchor');
    const behavior = scroller.style.getPropertyValue('scroll-behavior');
    const behaviorPriority = scroller.style.getPropertyPriority('scroll-behavior');
    scroller.style.setProperty('overflow-anchor', 'none', 'important');
    scroller.style.setProperty('scroll-behavior', 'auto', 'important');
    const top = summary.getBoundingClientRect().top;
    const opening = !panel.open;
    for (const other of this.querySelectorAll('.ml-purchase-support__information > details')) {
      if (other !== panel && other.closest('ml-product-accordions') === this) other.open = false;
    }
    panel.open = opening;
    const holdPosition = () => {
      const delta = summary.getBoundingClientRect().top - top;
      if (Math.abs(delta) > .5) scroller.scrollTop += delta;
    };
    holdPosition();
    const restore = () => {
      if (anchor) scroller.style.setProperty('overflow-anchor', anchor, anchorPriority);
      else scroller.style.removeProperty('overflow-anchor');
      if (behavior) scroller.style.setProperty('scroll-behavior', behavior, behaviorPriority);
      else scroller.style.removeProperty('scroll-behavior');
    };
    const frame = requestAnimationFrame(() => {
      if (this.isConnected) holdPosition();
      restore();
      this.restoreScroll = null;
    });
    this.restoreScroll = () => {
      cancelAnimationFrame(frame);
      restore();
      this.restoreScroll = null;
    };
  }
}

if (!customElements.get('ml-product-accordions')) customElements.define('ml-product-accordions', MLProductAccordions);
